/**
 * t413u: two-factor sign-in (TOTP, Supabase Auth). Shared by the web (desktop + phone) and the iOS app so the steps,
 * wording and error handling are identical. Supabase enrols and verifies factors; the API keeps recovery codes, the
 * audit trail and the workspace policy, and refuses aal1 tokens for anyone with a verified factor.
 */

export const MFA_POLICIES = ['off', 'admins', 'everyone'] as const;
export type MfaPolicy = (typeof MFA_POLICIES)[number];
export const MFA_POLICY_LABELS: Record<MfaPolicy, string> = {
  off: 'Optional',
  admins: 'Required for owners and admins',
  everyone: 'Required for everyone',
};

export interface MfaStatus {
  factors: number;
  enabled: boolean;
  recovery_codes_left: number;
  /** A workspace policy applies to this person. */
  required: boolean;
  /** …and they haven't set it up yet: the app shows the set-up step before anything else. */
  enrolment_required: boolean;
  /** t462u: who asks for it: an owner for this person ('owner'), the workspace setting ('workspace'), or no one. */
  required_by?: MfaRequiredBy | null;
}

/** t462u: an owner turned "Require two-factor sign-in" on for the person, or the workspace setting covers them. */
export type MfaRequiredBy = 'owner' | 'workspace';

/** t555u: app_lock_after_min, minutes away before the iOS app's Face ID lock asks again (src/appLock.ts). */
export interface SecurityPolicy { mfa_required: MfaPolicy; members: number; with_mfa: number; app_lock_after_min: import('./appLock').AppLockAfter }

/** API error codes. */
export const MFA_REQUIRED = 'mfa_required';
export const MFA_ENROLMENT_REQUIRED = 'mfa_enrolment_required';

/** Shown in authenticator apps next to the account. Neutral (no company name), like the sign-in screens. */
export const MFA_ISSUER = 'The Hub';

export const MFA_TEXT = {
  challengeTitle: 'Two-factor sign-in',
  challengeHint: 'Enter the 6-digit code from your authenticator app.',
  recoveryTitle: 'Use a recovery code',
  recoveryHint: 'Enter one of the recovery codes you saved when you set up two-factor. Two-factor will be turned off so you can set it up again.',
  badCode: "That code didn't work. Check your authenticator app and try again.",
  badRecovery: "That recovery code isn't valid. Check it and try again.",
  enrolTitle: 'Set up two-factor sign-in',
  enrolRequired: 'Your organisation needs you to set up two-factor sign-in before you continue.',
  /** t462u: iOS, a call answered from the lock screen while the set-up (or code) step is showing. */
  callWaiting: 'You answered a call. It connects once you’ve finished this step.',
  enrolRequiredByOwner: 'A workspace owner has asked you to set up two-factor sign-in before you continue.',
  enrolHint: 'Scan the QR code with an authenticator app (such as Google Authenticator, Microsoft Authenticator or 1Password), then enter the 6-digit code it shows.',
  manualKey: "Can't scan? Enter this key",
  savedCodes: 'Save these recovery codes somewhere safe. Each one works once, if you lose your phone.',
  removeHint: 'Enter a code from your authenticator app to turn two-factor off.',
} as const;

/** t462u: the set-up gate's line, by who asked for it. */
export const enrolRequiredText = (by: MfaRequiredBy | null | undefined) => (by === 'owner' ? MFA_TEXT.enrolRequiredByOwner : MFA_TEXT.enrolRequired);

/**
 * t462u: one person's two-factor, as a workspace Owner sees it on their Users page / person sheet. Only Owners get
 * this (the API answers 403 to everyone else), and only Owners can change `required`.
 */
export interface MemberTwoFactor {
  user_id: string;
  /** An owner turned "Require two-factor sign-in" on for this person. */
  required: boolean;
  /** The workspace setting (Sign-in security) covers them anyway. */
  required_by_workspace: boolean;
  /** They have a verified authenticator app. */
  set_up: boolean;
  /** They have signed in with Microsoft (SSO). */
  sso: boolean;
  /** They are a workspace Owner. */
  owner: boolean;
}

export const MEMBER_MFA_TEXT = {
  title: 'Two-factor sign-in',
  switchLabel: 'Require two-factor sign-in',
  setUp: 'Set up',
  notSetUp: 'Not set up yet',
  workspace: 'Also required by the workspace setting.',
  sso: 'Signs in with Microsoft. They still enter a code from an authenticator app here, as Microsoft’s own checks can’t be seen by The Hub.',
  reset: 'Reset two-factor sign-in',
  resetTitle: 'Reset two-factor sign-in?',
  resetBody: 'Use this if they lost their phone. Their authenticator app and recovery codes stop working, and they set it up again at their next sign-in.',
  bulkOn: 'Require two-factor sign-in',
  bulkOff: 'Stop requiring two-factor sign-in',
  selfFirst: 'Set up two-factor sign-in for yourself first, in My account › Security.',
} as const;

/** Muted status next to the switch: "Set up" / "Not set up yet". */
export const memberMfaStatusText = (m: Pick<MemberTwoFactor, 'set_up'>) => (m.set_up ? MEMBER_MFA_TEXT.setUp : MEMBER_MFA_TEXT.notSetUp);

/** The muted line under the switch (desktop, phone web and iOS): "Not set up yet. Signs in with Microsoft…". */
export function memberMfaNote(m: MemberTwoFactor, self: boolean): string {
  const extra = [
    m.required_by_workspace && !m.required ? MEMBER_MFA_TEXT.workspace : '',
    m.sso ? MEMBER_MFA_TEXT.sso : '',
    self && !m.set_up && !m.required ? MEMBER_MFA_TEXT.selfFirst : '',
  ].filter(Boolean);
  return [memberMfaStatusText(m), ...extra].join('. ').replace(/\.\. /g, '. ');
}

/** The owner's own switch can only go on once they use two-factor themselves (otherwise they'd be stopped at once). */
export const canRequireForSelf = (m: Pick<MemberTwoFactor, 'set_up'>) => m.set_up;

/** In-app notices about someone's two-factor (bell and push only: never emailed). */
export const MFA_NOTICE_KINDS = ['security.mfa_required', 'security.mfa_reset'] as const;
export const MFA_NOTICE = {
  requiredTitle: 'Two-factor sign-in is now required',
  requiredBodyNotSetUp: 'A workspace owner turned this on for your account. You’ll be asked to set it up before you continue.',
  requiredBodySetUp: 'A workspace owner turned this on for your account. Keep your authenticator app: you can’t turn two-factor sign-in off while it’s required.',
  resetTitle: 'Your two-factor sign-in was reset',
  resetBody: 'An owner or admin reset it. Set it up again in My account › Security. If you didn’t ask for this, tell an owner straight away.',
  link: '/settings/account/security',
} as const;

/** Keep only digits, at most 6 (pasting "123 456" works). */
export const cleanTotp = (v: string) => v.replace(/\D/g, '').slice(0, 6);
export const isTotp = (v: string) => /^\d{6}$/.test(v);

/** Recovery codes are case-insensitive, and spaces/dashes don't matter: "ABCDE FGHJK" = "abcde-fghjk". */
export function normaliseRecoveryCode(v: string): string {
  const s = v.toLowerCase().replace(/[^a-z0-9]/g, '');
  return s.length === 10 ? `${s.slice(0, 5)}-${s.slice(5)}` : s;
}

/** The QR code payload Supabase returns uses its own issuer; show ours and the person's email instead. */
export function otpauthUri(secret: string, email: string, issuer = MFA_ISSUER): string {
  const label = encodeURIComponent(`${issuer}:${email}`);
  return `otpauth://totp/${label}?secret=${encodeURIComponent(secret)}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`;
}

/** Group the manual key in fours so it can be typed. */
export const groupKey = (secret: string) => secret.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();

/** Short label for a factor in the list. */
export function factorLabel(f: { friendly_name?: string | null; created_at?: string }, locale = 'en-GB', timeZone = 'Europe/London'): string {
  const when = f.created_at ? new Date(f.created_at).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone }) : '';
  const name = f.friendly_name && !/^hub-/.test(f.friendly_name) ? f.friendly_name : 'Authenticator app';
  return when ? `${name} · added ${when}` : name;
}

/** A unique friendly name per enrolment (Supabase refuses duplicates, including abandoned unverified ones). */
export const newFactorName = (now = Date.now()) => `hub-${now.toString(36)}`;

/** Minimal slice of supabase-js `auth.mfa` used below (so web and iOS share the flow and it can be unit-tested). */
export interface MfaAuthClient {
  mfa: {
    getAuthenticatorAssuranceLevel(): Promise<{ data: { currentLevel: string | null; nextLevel: string | null } | null; error: unknown }>;
    listFactors(): Promise<{ data: { totp: Array<{ id: string; status: string; friendly_name?: string | null; created_at?: string }> } | null; error: unknown }>;
    challengeAndVerify(p: { factorId: string; code: string }): Promise<{ data: unknown; error: unknown }>;
  };
}

/** The `aal` claim of an access token (no signature check: only used to pick the screen; the API verifies). */
export function tokenAal(accessToken: string | null | undefined): string | null {
  try {
    const part = accessToken?.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const json = typeof atob === 'function' ? atob(b64) : '';
    const aal = (JSON.parse(json) as { aal?: unknown }).aal;
    return typeof aal === 'string' ? aal : null;
  } catch { return null; }
}

/**
 * Synchronous check on a Supabase session: signed in with the password only (aal1) but the account has a verified
 * factor, so the app shows the code step and nothing else.
 */
export function sessionNeedsMfa(s: { access_token: string; user?: { factors?: Array<{ status: string }> | null } } | null | undefined): boolean {
  if (!s) return false;
  const verified = (s.user?.factors ?? []).some(f => f.status === 'verified');
  return verified && tokenAal(s.access_token) !== 'aal2';
}

/** After the password: does this session still need the second step? */
export async function needsMfaChallenge(auth: MfaAuthClient): Promise<boolean> {
  const { data, error } = await auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return false;
  return data.currentLevel === 'aal1' && data.nextLevel === 'aal2';
}

export async function verifiedTotpFactors(auth: MfaAuthClient) {
  const { data, error } = await auth.mfa.listFactors();
  if (error) throw error;
  return (data?.totp ?? []).filter(f => f.status === 'verified');
}

/**
 * Check a code against the person's verified factors (normally one; with several, any of them). Returns true once the
 * session is aal2. Throws only for network/rate-limit problems; a wrong code returns false.
 */
export async function verifyTotpCode(auth: MfaAuthClient, code: string): Promise<boolean> {
  const factors = await verifiedTotpFactors(auth);
  let lastErr: unknown = null;
  for (const f of factors) {
    const { error } = await auth.mfa.challengeAndVerify({ factorId: f.id, code });
    if (!error) return true;
    lastErr = error;
    if (isTransientAuthError(error)) throw error;
  }
  void lastErr;
  return false;
}

export function isTransientAuthError(e: unknown): boolean {
  const err = (e ?? {}) as { status?: number; name?: string; message?: string };
  const m = typeof err.message === 'string' ? err.message : '';
  return err.status === 429 || /rate limit|too many/i.test(m) || err.name === 'AuthRetryableFetchError'
    || /failed to fetch|network ?error|network request failed|load failed/i.test(m);
}

/** What a failed code says. Never says whether the account or factor exists. */
export function mfaErrorMessage(e: unknown, offlineMessage: string): string {
  const err = (e ?? {}) as { status?: number; message?: string };
  const m = typeof err.message === 'string' ? err.message : '';
  if (err.status === 429 || /rate limit|too many/i.test(m)) return 'Too many attempts. Wait a minute and try again.';
  if (isTransientAuthError(e)) return offlineMessage;
  return MFA_TEXT.badCode;
}

/** The API's 403 when a token isn't aal2 / when the policy needs set-up first. */
export const apiErrorCode = (e: unknown): string | undefined => {
  const err = e as { code?: unknown; body?: { code?: unknown } } | null;
  const c = err?.code ?? err?.body?.code;
  return typeof c === 'string' ? c : undefined;
};

/** t462u: the API's two-factor 403 codes (sign-in code still needed, or set-up needed first). */
export function mfaBlockCode(body: unknown): 'mfa_required' | 'mfa_enrolment_required' | null {
  const c = body && typeof body === 'object' ? (body as { code?: unknown }).code : null;
  return c === 'mfa_required' || c === 'mfa_enrolment_required' ? c : null;
}
